import type { FillStyle, Gradient, LineStyle, TextRun } from '@/api/types/stowed-element'
import {
  DEFAULT_FONT_FAMILY_ID,
  DEFAULT_FONT_ID,
  DEFAULT_POINT_SIZE,
} from '@/lib/text/constants/text-defaults'
import type { TextRunStyle } from '@/lib/text/types/text-run-style'

/**
 * The canonical set of run style fields. Exported so anything that copies run style can be
 * asserted against it — `blankParagraphFromExisting` hand-listed nine of these ten and silently
 * dropped `strikethrough`; see `blank-paragraph.test.ts`.
 */
export const RUN_STYLE_KEYS: (keyof TextRunStyle)[] = [
  'fontId',
  'fontFamilyId',
  'pointSize',
  'underline',
  'strikethrough',
  'kerning',
  'tracking',
  'leading',
  'lineStyle',
  'fillStyle',
  'verticalAlign',
]

/** Pull the style-defining fields off a run (defaults applied when the run is missing). */
export function extractRunStyle(run: TextRun | undefined): TextRunStyle {
  if (!run) {
    return {
      pointSize: DEFAULT_POINT_SIZE,
      fontId: DEFAULT_FONT_ID,
      fontFamilyId: DEFAULT_FONT_FAMILY_ID,
    }
  }

  const style: TextRunStyle = {}

  if (run.fontId != null) style.fontId = run.fontId
  if (run.fontFamilyId != null) style.fontFamilyId = run.fontFamilyId
  if (run.pointSize != null) style.pointSize = run.pointSize
  if (run.underline != null) style.underline = run.underline
  if (run.strikethrough != null) style.strikethrough = run.strikethrough
  if (run.kerning != null) style.kerning = run.kerning
  if (run.tracking != null) style.tracking = run.tracking
  if (run.leading != null) style.leading = run.leading
  if (run.lineStyle != null) style.lineStyle = run.lineStyle
  if (run.fillStyle != null) style.fillStyle = run.fillStyle
  if (run.verticalAlign != null) style.verticalAlign = run.verticalAlign

  return style
}

function gradientEquals(a: Gradient | undefined, b: Gradient | undefined): boolean {
  return (
    a?.startColor === b?.startColor &&
    a?.endColor === b?.endColor &&
    a?.gradientStyle === b?.gradientStyle
  )
}

/** Plain per-field comparison — no default substitution. `undefined` only equals `undefined`, so
 *  two never-styled runs (both undefined) still merge, but a loaded/explicit style is never
 *  silently treated as equal to "no style" (there is no single default color safe to assume — see
 *  `stowed-element-builder.ts`'s `ensureTextRunDefaults`). */
function lineStyleEquals(a: LineStyle | undefined, b: LineStyle | undefined): boolean {
  return (
    a?.weight === b?.weight &&
    a?.colorId === b?.colorId &&
    a?.patternId === b?.patternId &&
    a?.tint === b?.tint
  )
}

function fillStyleEquals(a: FillStyle | undefined, b: FillStyle | undefined): boolean {
  return (
    a?.colorId === b?.colorId && a?.tint === b?.tint && gradientEquals(a?.gradient, b?.gradient)
  )
}

/**
 * Structural equality for run styles, treating `underline`/`strikethrough: undefined` as `false`
 * and `kerning`/`tracking: undefined` as `0` — both default to "no adjustment" when unset, so an
 * explicit 0 (e.g. the Natural chip) must merge back with neighboring runs that never had the
 * field set, instead of leaving a permanent single-character run fragment behind.
 */
export function runStyleEquals(a: TextRunStyle, b: TextRunStyle): boolean {
  for (const key of RUN_STYLE_KEYS) {
    if (key === 'underline') {
      if (Boolean(a.underline) !== Boolean(b.underline)) return false
      continue
    }

    if (key === 'strikethrough') {
      if (Boolean(a.strikethrough) !== Boolean(b.strikethrough)) return false
      continue
    }

    if (key === 'kerning' || key === 'tracking') {
      if ((a[key] ?? 0) !== (b[key] ?? 0)) return false
      continue
    }

    if (key === 'lineStyle') {
      if (!lineStyleEquals(a.lineStyle, b.lineStyle)) return false
      continue
    }

    if (key === 'fillStyle') {
      if (!fillStyleEquals(a.fillStyle, b.fillStyle)) return false
      continue
    }

    if ((a[key] ?? undefined) !== (b[key] ?? undefined)) return false
  }

  return true
}
